// Header "active jobs" tray — a persistent view of every in-flight render
// (music / image / video / bundle / visualizer) regardless of which screen
// kicked it off. Polls the Renders "active" feed and overlays live SSE
// progress; renders nothing when the queue is empty so it only shows up while
// something is actually working. Clicking a row jumps to Renders → Active.
//
// This is the answer to "any simple navigation made the queued job vanish":
// jobs always live server-side, and this tray surfaces them everywhere.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dot } from "../../lib/brand";
import { services } from "../../services";
import { useRenderProgress } from "../../hooks/useRenderProgress";
import type { RenderJob } from "../../types";

export function ActiveJobsTray({ compact = false }: { compact?: boolean }) {
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  // Poll the active feed every 5s — the list is small and this is the source
  // of truth for WHICH jobs are in flight.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const rows = await services.renders.list("active");
        if (!cancelled) setJobs(rows);
      } catch {
        /* transient — keep the last snapshot */
      }
    };
    void tick();
    const t = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Live progress overlay — only stream while something is rendering.
  const progress = useRenderProgress(jobs.length > 0);

  // Close on click-outside / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Merge live SSE frames over the polled rows for smooth progress.
  const rows = jobs.map((j) => {
    const live = progress[j.id];
    return live ? { ...j, progress: live.progress, state: live.state, elapsed: live.elapsed } : j;
  });

  if (rows.length === 0) return null;
  const count = rows.length;

  const goActive = () => {
    setOpen(false);
    navigate("/renders?tab=active");
  };
  // Open a specific render's detail sheet. RendersPage consumes ``?render=``
  // and auto-opens the matching active row's sheet.
  const openRender = (id: string) => {
    setOpen(false);
    navigate(`/renders?tab=active&render=${encodeURIComponent(id)}`);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {compact ? (
        // Mobile: a 32×32 icon-style button (pulse dot + count badge) so it
        // fits the tight header next to the bell.
        <button
          aria-label={`${count} render${count === 1 ? "" : "s"} in progress`}
          onClick={() => setOpen((o) => !o)}
          style={{
            position: "relative",
            width: 32,
            height: 32,
            borderRadius: 9999,
            border: "1px solid #1845FF",
            background: "rgba(24,69,255,0.10)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 9999,
              background: "#1845FF",
              animation: "vocPulse 1.4s ease-in-out infinite",
            }}
          />
          <span
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 9999,
              background: "#1845FF",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {count > 9 ? "9+" : count}
          </span>
        </button>
      ) : (
        <button
          aria-label={`${count} render${count === 1 ? "" : "s"} in progress`}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            height: 32,
            padding: "0 12px",
            borderRadius: 9999,
            border: "1px solid #1845FF",
            background: "rgba(24,69,255,0.10)",
            color: "var(--fg)",
            fontSize: 12,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 9999,
              background: "#1845FF",
              animation: "vocPulse 1.4s ease-in-out infinite",
            }}
          />
          {count} rendering
        </button>
      )}

      {open && (
        <div
          style={{
            position: "absolute",
            top: 40,
            right: 0,
            width: "min(320px, calc(100vw - 24px))",
            maxHeight: 420,
            overflowY: "auto",
            background: "var(--bg-elev, #16161a)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid var(--border)",
              fontSize: 13,
              fontWeight: 500,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Rendering now</span>
            <button
              onClick={goActive}
              style={{
                background: "transparent",
                border: "none",
                color: "#1845FF",
                fontSize: 12,
                cursor: "pointer",
                padding: 0,
              }}
            >
              View all →
            </button>
          </div>
          {rows.map((j) => (
            <button
              key={j.id}
              onClick={() => openRender(j.id)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                background: "transparent",
                border: "none",
                borderBottom: "1px solid var(--border)",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Dot state={j.state} size={7} />
                <span
                  style={{
                    fontSize: 13,
                    color: "var(--fg)",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {j.title || "Untitled"}
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-subtle)" }}>
                  {j.type}
                </span>
              </div>
              {/* Progress as movement, not a number — a thin bar plus elapsed
                  time, so it never reads as "stuck at N%". */}
              <div style={{ height: 4, background: "var(--border)", borderRadius: 9999, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.max(4, Math.min(100, j.progress || 0))}%`,
                    height: "100%",
                    background: "#1845FF",
                    transition: "width 400ms ease",
                  }}
                />
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-subtle)", marginTop: 4 }}>
                {j.state === "queued" ? "queued" : "rendering"}
                {j.elapsed ? ` · ${j.elapsed}` : ""}
              </div>
            </button>
          ))}
          <button
            onClick={goActive}
            style={{
              display: "block",
              width: "100%",
              textAlign: "center",
              padding: "10px 14px",
              background: "transparent",
              border: "none",
              color: "var(--fg-muted)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            You can leave this page — we'll notify you when each is ready.
          </button>
        </div>
      )}
    </div>
  );
}
